import { createFocusTrap, type FocusTrap } from 'focus-trap';
import { defineModule } from '../../utils/helpers';
import { Overlay, toggleOverlay } from '../../utils/overlays';

let focusTrap: FocusTrap | null = null;

export type ModalId = 'sign-up' | 'schedule-demo';

const CALENDLY_ORIGIN = 'https://calendly.com';

const getElements = () => ({
  openModalElements:
    document.querySelectorAll<HTMLElement>('[data-open-modal]'),
  dismissModalElements: document.querySelectorAll<HTMLElement>(
    '[data-dismiss-modal]',
  ),
  toggleModalElements: document.querySelectorAll<HTMLElement>(
    '[data-toggle-modal]',
  ),
  calendlyWidgetElement: document.querySelector<HTMLElement>(
    '#calendly-inline-widget',
  ),
});

/**
 * Size the agenda to the view Calendly is showing, since its iframe has no
 * intrinsic height and a fixed one clips the taller views. The widget's
 * `min-height` floors the near-zero heights reported while it boots.
 */
const handleCalendlyMessage = (e: MessageEvent) => {
  if (e.origin !== CALENDLY_ORIGIN) return;
  if (typeof e.data !== 'object' || e.data === null) return;

  const { event, payload } = e.data as {
    event?: string;
    payload?: { height?: string };
  };
  if (event !== 'calendly.page_height' || !payload?.height) return;

  const { calendlyWidgetElement } = getElements();
  if (calendlyWidgetElement) {
    calendlyWidgetElement.style.height = payload.height;
  }
};

/**
 * Name the agenda frame. Calendly builds the iframe itself and leaves it
 * untitled, so a screen reader announces it as an unnamed frame; the element
 * only lands in the DOM once the widget has booted, hence the observer.
 */
const setCalendlyFrameTitle = (widgetElement: HTMLElement) => {
  const { calendlyTitle } = widgetElement.dataset;
  if (!calendlyTitle) return;

  const applyTitle = () => {
    const frameElement = widgetElement.querySelector('iframe');
    if (!frameElement) return false;

    frameElement.title = calendlyTitle;
    return true;
  };

  if (applyTitle()) return;

  const observer = new MutationObserver(() => {
    if (applyTitle()) observer.disconnect();
  });

  observer.observe(widgetElement, { childList: true, subtree: true });
};

/**
 * Open or close a modal. Returns whether the modal exists — a template can skip
 * rendering itself (the sign-up overlay does when no form is selected), and
 * callers that swallow a click need to know before they do.
 */
export const toggleModal = (id: ModalId, force?: boolean) => {
  const modalElement = document.querySelector<HTMLElement>(
    `[data-modal="${id}"]`,
  );
  if (!modalElement) return false;

  const modalContentElement =
    modalElement.querySelector<HTMLElement>('.modal__content');
  if (!modalContentElement) return false;

  force = force ?? !modalElement.classList.contains('modal--open');

  // One modal at a time: `focusTrap` holds a single trap, so a second one would
  // leave the first active, and closing either would unlock the body scroll
  // while the other is still open.
  if (force) {
    document
      .querySelectorAll<HTMLElement>('.modal--open')
      .forEach((openModalElement) => {
        const openId = openModalElement.getAttribute('data-modal');
        if (openId && openId !== id) toggleModal(openId as ModalId, false);
      });
  }

  toggleOverlay(Overlay.MODAL, force);
  modalElement.classList.toggle('modal--open', force);

  if (force) {
    modalElement.setAttribute('aria-modal', 'true');
    modalElement.removeAttribute('aria-hidden');

    if (id === 'schedule-demo') {
      const { calendlyWidgetElement } = getElements();

      // initInlineWidget() appends rather than replaces, so re-opening would
      // stack a second agenda underneath the first.
      if (
        calendlyWidgetElement &&
        !calendlyWidgetElement.querySelector('iframe') &&
        typeof Calendly !== 'undefined'
      ) {
        const { calendlyUrl } = calendlyWidgetElement.dataset;
        if (calendlyUrl) {
          const calendlyOptions = {
            url: calendlyUrl,
            parentElement: calendlyWidgetElement,
            prefill: {},
            utm: {},
          };

          // https://help.calendly.com/hc/en-us/articles/223147027?tab=advanced
          Calendly.initInlineWidget(calendlyOptions);

          setCalendlyFrameTitle(calendlyWidgetElement);
        }
      }
    }

    // Trap right away so tabbing can never reach the page behind the modal.
    // `visibility` is still mid-transition at this point, which makes every node
    // inside untabbable — hence fallbackFocus, without which activation throws
    // and no trap gets installed at all. Focus lands on the panel once it is
    // visible, unless the visitor already tabbed into a field by then.
    focusTrap = createFocusTrap(modalElement, {
      // `.modal` takes no focus itself, so focus-trap fell back to the first
      // tabbable node — the close button, which a screen reader then announces
      // as "Sluiten" before anything about the dialog. `.modal__content` carries
      // a `tabindex="-1"` for exactly this: land on the dialog, read from the
      // top, and Tab moves on to the close button.
      initialFocus: modalContentElement,
      fallbackFocus: modalContentElement,
      allowOutsideClick: true,
      // Escape is handled below so the modal's own state is updated with it.
      escapeDeactivates: false,
      preventScroll: true,
    });

    focusTrap.activate();

    modalContentElement.addEventListener(
      'transitionend',
      () => {
        // Closed again before the transition finished: deactivate() has already
        // handed focus back to the trigger, and the panel is hidden by now, so
        // focusing it would only drop focus onto <body>.
        if (!modalElement.classList.contains('modal--open')) return;
        if (modalElement.contains(document.activeElement)) return;

        modalContentElement.focus({ preventScroll: true });
      },
      { once: true, passive: true },
    );
  } else {
    modalElement.removeAttribute('aria-modal');
    modalElement.setAttribute('aria-hidden', 'true');

    focusTrap?.deactivate();
  }

  return true;
};

const handleOutsideModalContentClick = (e: Event) => {
  if (!(e.target instanceof HTMLElement)) return;

  const isOutsideClick = !e.target.closest<HTMLElement>('.modal__content');
  if (isOutsideClick) {
    const modalElement = e.target.closest<HTMLElement>('.modal');
    if (!modalElement) return;

    const modalId = modalElement.getAttribute('data-modal');
    if (!modalId) return;

    toggleModal(modalId as ModalId, false);
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key !== 'Escape') return;

  const modalId = document
    .querySelector<HTMLElement>('.modal--open')
    ?.getAttribute('data-modal');
  if (!modalId) return;

  toggleModal(modalId as ModalId, false);
};

const handleModalEvent = (e: Event) => {
  if (!(e.currentTarget instanceof HTMLElement)) return;

  const [shouldOpen, shouldClose] = [
    'data-open-modal',
    'data-dismiss-modal',
  ].map((attr) => (e.currentTarget as HTMLElement).getAttribute(attr));

  const targetModal = shouldOpen || shouldClose;
  if (!targetModal) return;

  toggleModal(
    targetModal as ModalId,
    // eslint-disable-next-line no-nested-ternary
    shouldOpen ? true : shouldClose ? false : undefined,
  );
};

export default defineModule(
  () => {
    const { openModalElements, dismissModalElements, toggleModalElements } =
      getElements();

    [
      ...openModalElements,
      ...dismissModalElements,
      ...toggleModalElements,
    ].forEach((openModalElement) =>
      openModalElement.addEventListener('click', handleModalEvent, {
        passive: true,
      }),
    );

    document.addEventListener('click', handleOutsideModalContentClick, {
      passive: true,
    });
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('message', handleCalendlyMessage);
  },
  () => {
    const { openModalElements, dismissModalElements, toggleModalElements } =
      getElements();

    [
      ...openModalElements,
      ...dismissModalElements,
      ...toggleModalElements,
    ].forEach((openModalElement) =>
      openModalElement.removeEventListener('click', handleModalEvent),
    );

    focusTrap?.deactivate();

    document.removeEventListener('click', handleOutsideModalContentClick);
    window.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('message', handleCalendlyMessage);
  },
);
